import { Injectable, Injector } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import * as jwt_decode from 'jwt-decode';
import { TranslateService } from '@ngx-translate/core';
import { UserLoggedIn } from '../models/user-login.model';
import { environment } from 'src/environments/environment';
import { UserService } from 'src/app/modules/users/services/user.service';
import { UserRoleData } from '../models/user-role-.model';
import { LanguageService } from './language';
import { UmRole } from 'src/app/modules/roles/models/role-enum';

@Injectable({
  providedIn: 'root',
})
export class CurrentUserService {
  private currentUserKey: string = 'currentUser';
  private tokenExchangeCompleted = new BehaviorSubject<boolean>(false);

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private injector: Injector
  ) {
    // Initialize token exchange status based on existing user data
    const user = this.getCurrentUser();
    this.tokenExchangeCompleted.next(user !== null);
  }

  isLoggedIn(): boolean {
    let user = this.getCurrentUser();
    return user !== null;
  }

  getCurrentUser(): UserLoggedIn | null {
    let item = localStorage.getItem(this.currentUserKey);
    return item ? JSON.parse(item) : null;
  }
  getCurrentUserRole(): number | null {
    let user = this.getCurrentUser();
    return user ? user.roleId : null;
  }

  getCurrentUserIdString(): string | null {
    let user = this.getCurrentUser();
    if (user) {
      let token = jwt_decode.jwtDecode(user.access_Token) as any;
      if (token) {
        return token.unique_name;
      }
    }
    return null;
  }

  /**
   * Logout user and clear session
   * @param showAuthError Whether to show auth error notification
   * @param logoutAllDevices Whether to logout from all devices
   * @param skipServerCall Skip server logout call (use when tokens are already invalid)
   */
  logOut(
    showAuthError: boolean = false,
    logoutAllDevices: boolean = false,
    skipServerCall: boolean = false
  ) {
    if (showAuthError) {
      // Handle error notification if needed
    }

    // Get refresh token and return URL before clearing storage
    const refreshToken = this.getRefreshToken();
    const returnUrl = sessionStorage.getItem('returnUrl');

    // Skip server call if:
    // 1. No refresh token exists
    // 2. Explicitly told to skip (tokens are already invalid/expired)
    if (!refreshToken || skipServerCall) {
      if (!refreshToken) {
        console.warn('No refresh token to revoke, clearing local data only');
      } else {
        console.warn(
          'Skipping server logout call (tokens invalid), clearing local data only'
        );
      }
      this.clearLocalData(returnUrl);
      return;
    }

    // Call backend logout endpoint to revoke tokens
    // SECURITY FIX: VAPT Issue #3 - Iteration 1
    // Use dynamic import to avoid circular dependency
    import('../../modules/users/services/login.service').then(
      module => {
        try {
          const LoginService = module.LoginService;
          const loginService = this.injector.get(LoginService);

          // Call logout API to revoke refresh token
          loginService.logout(refreshToken, logoutAllDevices).subscribe({
            next: () => {
              console.log('✅ Logout API call successful');
              this.clearLocalData(returnUrl);
            },
            error: err => {
              console.error('❌ Logout API call failed:', err);
              // Even if logout fails on backend, clear local data
              this.clearLocalData(returnUrl);
            },
          });
        } catch (error) {
          console.error('❌ Error getting LoginService:', error);
          this.clearLocalData(returnUrl);
        }
      },
      error => {
        console.error('❌ Error importing LoginService:', error);
        // If service cannot be loaded, just clear local data
        this.clearLocalData(returnUrl);
      }
    );
  }

  /**
   * Clear local user data and navigate to login
   * SECURITY FIX: VAPT Issue #3 - Helper method for logout
   */
  private clearLocalData(returnUrl: string | null) {
    // Clear user data
    localStorage.removeItem(this.currentUserKey);
    this.tokenExchangeCompleted.next(false);

    // Clear module selection state
    sessionStorage.removeItem('selectedModule');
    sessionStorage.removeItem('moduleSelected');

    // Clear temporary login credentials (used for module/role selection)
    sessionStorage.removeItem('tempLoginCredentials');

    // Navigate to Angular login page
    if (returnUrl) {
      this.router.navigate(['/login'], {
        queryParams: { returnUrl: returnUrl },
      });
    } else {
      this.router.navigate(['/login']);
    }
  }

  getToken(): string | null {
    let user = this.getCurrentUser();
    return user ? user.access_Token : null;
  }

  /**
   * Get the refresh token for the current user
   * SECURITY FIX: VAPT Issue #3 - Iteration 1
   */
  getRefreshToken(): string | null {
    let user = this.getCurrentUser();
    if (!user || !user.refresh_Token) {
      console.warn('No refresh token found for current user');
      return null;
    }
    return user.refresh_Token;
  }

  /**
   * Update the access token (used after token refresh)
   * SECURITY FIX: VAPT Issue #3 - Iteration 1
   */
  updateTokens(accessToken: string, refreshToken?: string) {
    let user = this.getCurrentUser();
    if (user) {
      user.access_Token = accessToken;
      if (refreshToken) {
        user.refresh_Token = refreshToken;
      }
      this.setCurrentUser(user);
    }
  }

  // New: Observable to check if token exchange is completed
  isTokenExchangeCompleted(): Observable<boolean> {
    return this.tokenExchangeCompleted.asObservable();
  }

  setCurrentUser(user: UserLoggedIn | null) {
    if (user) {
      localStorage.setItem(this.currentUserKey, JSON.stringify(user));
      this.tokenExchangeCompleted.next(true);
      // When an agent logs in, swap the UI to their preferred evaluation
      // language. Non-agents keep whatever the user picked manually. Done
      // here (the single choke-point for "user is now signed in") so it
      // applies regardless of login path — username/password, SSO callback,
      // or module-selection follow-up.
      this.applyAgentEvaluationLanguage(user);
    } else {
      localStorage.removeItem(this.currentUserKey);
      this.tokenExchangeCompleted.next(false);
    }
  }

  /**
   * If the freshly-set user is an agent and carries an `evaluationLanguage`,
   * apply it via TranslateService + LanguageService and persist the choice
   * so it survives a page reload. Silently no-ops for non-agents or when
   * the field is unset.
   */
  private applyAgentEvaluationLanguage(user: UserLoggedIn): void {
    if (user.roleId !== UmRole.Agent) return;
    const lang = (user.evaluationLanguage || '').trim().toLowerCase();
    if (lang !== 'en' && lang !== 'ar') return;
    try {
      const translateService = this.injector.get(TranslateService);
      const languageService = this.injector.get(LanguageService);
      translateService.use(lang);
      languageService.setLanguage(lang);
      localStorage.setItem('currentLang', lang);
    } catch (err) {
      // Service resolution can fail during very early bootstrap — fall back
      // to writing the storage key so the next reload picks it up.
      console.warn('Failed to apply agent evaluation language live', err);
      localStorage.setItem('currentLang', lang);
    }
  }

  /**
   * Check if the current user has multiple modules
   * Note: This is a placeholder implementation. You may need to adjust based on
   * how multiple modules are indicated in your system (API response, JWT token, etc.)
   */
  hasMultipleModules(): boolean {
    const user = this.getCurrentUser();
    if (!user) return false;

    // For now, we'll use a conservative approach and assume single module
    // You can modify this logic based on your actual requirements:
    // Option 1: Check JWT token for module information
    // Option 2: Make an API call to check user modules
    // Option 3: Store module info in user object during login

    // Placeholder logic - always return false for now
    // This means the Angular app won't enforce module selection
    // The MVC backend will handle this instead
    return false;
  }

  /**
   * Check if user has selected a module for this session
   */
  hasSelectedModule(): boolean {
    return (
      sessionStorage.getItem('selectedModule') !== null ||
      sessionStorage.getItem('moduleSelected') === 'true'
    );
  }

  /**
   * Set module selection state
   */
  setModuleSelected(moduleId?: string): void {
    sessionStorage.setItem('moduleSelected', 'true');
    if (moduleId) {
      sessionStorage.setItem('selectedModule', moduleId);
    }
  }

  /**
   * Clear module selection state
   */
  clearModuleSelection(): void {
    sessionStorage.removeItem('selectedModule');
    sessionStorage.removeItem('moduleSelected');
  }

  /**
   * Get the stored return URL
   */
  getReturnUrl(): string | null {
    const url = sessionStorage.getItem('returnUrl');
    return url;
  }

  /**
   * Set return URL
   */
  setReturnUrl(url: string): void {
    sessionStorage.setItem('returnUrl', url);
  }

  /**
   * Clear return URL
   */
  clearReturnUrl(): void {
    sessionStorage.removeItem('returnUrl');
  }
}
